/**
 * Modo discreto (FUN 2026-10-02): censura de dados sensíveis na tela, como o "olho" dos apps de banco.
 *
 * Estado por aparelho em `localStorage` (`malgoos.discreto` = "1"), lido de forma síncrona no carregamento do módulo: o
 * primeiro render já sai censurado (o `public/tema.js` marca `<html data-discreto>` antes do paint). A troca vale em todas as
 * abas pelo evento `storage`. Os formatadores de `formato.ts` consultam `discretoAtivo()`; o i18n repassa a troca como o evento
 * `discreto` (bindI18n), então todo componente com `useTranslation` renderiza de novo sem recarregar a página.
 *
 * Limite consciente: é proteção contra quem olha a tela, não controle de acesso. Os dados continuam chegando ao navegador.
 */
export const CHAVE_DISCRETO = 'malgoos.discreto';
export const CHAVE_PREFS_DISCRETO = 'malgoos.discreto.prefs';
/** Última interação com o app em QUALQUER aba (ms desde a época): a inatividade conta pelo aparelho, não pela aba. */
export const CHAVE_ATIVIDADE_DISCRETO = 'malgoos.discreto.atividade';

/** Minutos sem interação até ligar sozinho (0 = desligado). */
export const OPCOES_INATIVIDADE = [0, 1, 5, 15] as const;
export type MinutosInatividade = (typeof OPCOES_INATIVIDADE)[number];

export interface PrefsDiscreto {
  inatividade: MinutosInatividade;
  /** Censura também nomes de pessoas e empresas (mostra as iniciais). */
  nomes: boolean;
}

const PREFS_PADRAO: PrefsDiscreto = { inatividade: 0, nomes: false };

function ler(chave: string): string | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage.getItem(chave);
  } catch {
    return null;
  }
}

function gravar(chave: string, valor: string | null) {
  try {
    if (valor === null) localStorage.removeItem(chave);
    else localStorage.setItem(chave, valor);
  } catch {
    /* sem storage: vale só nesta aba */
  }
}

function lerPrefs(bruto: string | null): PrefsDiscreto {
  if (!bruto) return PREFS_PADRAO;
  try {
    const p = JSON.parse(bruto) as Partial<PrefsDiscreto>;
    const inatividade = OPCOES_INATIVIDADE.includes(p.inatividade as MinutosInatividade) ? (p.inatividade as MinutosInatividade) : 0;
    return { inatividade, nomes: p.nomes === true };
  } catch {
    return PREFS_PADRAO;
  }
}

let ativo = ler(CHAVE_DISCRETO) === '1';
let prefs = lerPrefs(ler(CHAVE_PREFS_DISCRETO));
const ouvintes = new Set<() => void>();

function marcarHtml() {
  if (typeof document === 'undefined') return;
  if (ativo) document.documentElement.dataset.discreto = '';
  else delete document.documentElement.dataset.discreto;
}

function avisar() {
  marcarHtml();
  for (const o of [...ouvintes]) o();
}

export const discretoAtivo = () => ativo;
export const prefsDiscreto = () => prefs;
/** Censura de nomes vale só com o modo ligado e a opção marcada. */
export const nomesOcultos = () => ativo && prefs.nomes;

export function definirDiscreto(v: boolean) {
  if (v === ativo) return;
  ativo = v;
  // Desligar à mão é atividade: o relógio da inatividade recomeça em todas as abas (r9 spec M2).
  if (!v) marcarAtividade(Date.now(), true);
  gravar(CHAVE_DISCRETO, v ? '1' : null);
  avisar();
}

/* Inatividade compartilhada entre as abas (r9 spec M2): cada aba grava a última atividade em `localStorage` no máximo uma vez
   por segundo e a decisão usa a mais recente entre todas. Antes, a aba parada só via a própria atividade e ligava o modo
   enquanto a pessoa trabalhava em outra aba (e o religava logo depois de ela desligar). */
const INTERVALO_GRAVACAO_MS = 1_000;
let atividadeLocal = Date.now();
let gravadaEm = 0;

/** Registra interação nesta aba; grava para as outras com throttle (`forcar` grava já). */
export function marcarAtividade(agora = Date.now(), forcar = false) {
  atividadeLocal = Math.max(atividadeLocal, agora);
  if (forcar || agora - gravadaEm >= INTERVALO_GRAVACAO_MS) {
    gravadaEm = agora;
    gravar(CHAVE_ATIVIDADE_DISCRETO, String(atividadeLocal));
  }
}

/** Última atividade conhecida no aparelho: a desta aba ou a gravada por qualquer outra, a mais recente. */
export function ultimaAtividade() {
  const outra = Number(ler(CHAVE_ATIVIDADE_DISCRETO));
  return Number.isFinite(outra) ? Math.max(atividadeLocal, outra) : atividadeLocal;
}

/** Liga o modo se o aparelho inteiro está parado há o tempo da preferência. Devolve se ligou. */
export function conferirInatividade(agora = Date.now()) {
  const min = prefs.inatividade;
  if (min <= 0 || ativo || agora - ultimaAtividade() < min * 60_000) return false;
  definirDiscreto(true);
  return true;
}

export const alternarDiscreto = () => definirDiscreto(!ativo);

/**
 * Review r23 visual (M4): modo imposto pela sessão (o "Ver como o cliente" no portal, que abre em outra origem, sem o
 * `localStorage` da equipe). Vale só na memória desta aba: não grava no aparelho, não liga por inatividade e não segue a troca
 * de outra aba (o `storage` da origem do portal não manda nesta sessão).
 */
let fixado = false;
export function fixarDiscreto(v: { ativo: boolean; nomes: boolean } | null) {
  // Nulo solta a trava (sessão do próprio cliente; testes): o modo volta a seguir o aparelho na próxima troca.
  if (v === null) {
    fixado = false;
    return;
  }
  fixado = true;
  const mudou = v.ativo !== ativo || v.nomes !== prefs.nomes;
  ativo = v.ativo;
  prefs = { inatividade: 0, nomes: v.nomes };
  if (mudou) avisar();
  else marcarHtml();
}

export function definirPrefsDiscreto(p: Partial<PrefsDiscreto>) {
  prefs = lerPrefs(JSON.stringify({ ...prefs, ...p }));
  gravar(CHAVE_PREFS_DISCRETO, JSON.stringify(prefs));
  avisar();
}

/** Assina as trocas (modo e preferências); devolve o cancelamento. Serve ao `useSyncExternalStore`. */
export function assinarDiscreto(f: () => void) {
  ouvintes.add(f);
  return () => {
    ouvintes.delete(f);
  };
}

/** Outra aba trocou o modo ou as preferências: aplica aqui também. */
export function aoMudarStorage(chave: string | null, novo: string | null) {
  if (fixado) return;
  if (chave === CHAVE_DISCRETO || chave === null) {
    const v = (chave === null ? ler(CHAVE_DISCRETO) : novo) === '1';
    if (v !== ativo) {
      ativo = v;
      avisar();
    }
  }
  if (chave === CHAVE_PREFS_DISCRETO || chave === null) {
    const p = lerPrefs(chave === null ? ler(CHAVE_PREFS_DISCRETO) : novo);
    if (p.inatividade !== prefs.inatividade || p.nomes !== prefs.nomes) {
      prefs = p;
      avisar();
    }
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => aoMudarStorage(e.key, e.newValue));
  marcarHtml();
}

/* ------------------------------------------------------------------------------------------------------------------------ */
/* Máscaras puras (sem estado), testadas em tests/discreto.test.ts.                                                         */
/* ------------------------------------------------------------------------------------------------------------------------ */

/** Marca visível de valor oculto. */
export const OCULTO = '•••••';
const P = '•';

/** Troca os dígitos por "•", menos os índices de dígito em `manter` (contados da esquerda, 0 = primeiro dígito). */
function trocarDigitos(texto: string, manter: (i: number, total: number) => boolean) {
  const total = (texto.match(/\d/g) ?? []).length;
  let i = -1;
  return texto.replace(/\d/g, (d) => {
    i++;
    return manter(i, total) ? d : P;
  });
}

/**
 * CPF `•••.456.789-••`, CNPJ `••.345.678/0001-••`: some o começo e o dígito verificador. Outro documento: só os 4 do meio.
 * Review r16 visual B8: documento estrangeiro com letras e dígitos (RFC "GODE561231GR8", registro "ABCDEF12", "PT 509 876 543")
 * some inteiro: trocar só os dígitos deixava as letras, que identificam a pessoa, à vista.
 */
export function mascararDocumento(formatado: string) {
  const digitos = (formatado.match(/\d/g) ?? []).length;
  if (digitos && /\p{L}/u.test(formatado)) return OCULTO;
  if (digitos === 11) return trocarDigitos(formatado, (i) => i >= 3 && i < 9);
  if (digitos === 14) return trocarDigitos(formatado, (i) => i >= 2 && i < 12);
  return trocarDigitos(formatado, (i, n) => n > 6 && i >= n - 6 && i < n - 2);
}

/**
 * Telefone: só os 4 últimos dígitos. rl0211-10: com letra não é número (usuário do WhatsApp, por exemplo) e as letras identificam a
 * pessoa, então vira o marcador, como em `mascararDocumento`.
 */
export const mascararTelefone = (formatado: string) => (/\p{L}/u.test(formatado) ? OCULTO : trocarDigitos(formatado, (i, n) => i >= n - 4));

/**
 * Segundo nível genérico sob um domínio de país ("com.br", "co.uk", "gob.mx", "org.ar"): fica à vista junto com o país, porque é
 * sufixo público e não diz de quem é o e-mail.
 */
const SEGUNDO_NIVEL_PUBLICO = new Set(['com', 'net', 'org', 'gov', 'gob', 'edu', 'mil', 'co', 'ac', 'or', 'ne', 'go', 'nom', 'adv', 'art', 'eng', 'ind', 'inf', 'med', 'srv', 'tur', 'blog', 'emp', 'jus', 'leg', 'mp', 'coop', 'ltd', 'plc', 'biz', 'info']);

/** Quantos rótulos do fim do domínio são sufixo público (TLD ou eTLD de 2 níveis): 1 ou 2. */
function rotulosDoSufixo(partes: string[]) {
  if (partes.length < 3) return Math.min(1, partes.length - 1);
  const pais = partes[partes.length - 1]!;
  const segundo = partes[partes.length - 2]!.toLowerCase();
  return /^\p{L}{2}$/u.test(pais) && SEGUNDO_NIVEL_PUBLICO.has(segundo) ? 2 : 1;
}

/**
 * E-mail `g•••@d•••.com`: primeira letra do usuário e de CADA rótulo do domínio, e o sufixo público à vista (review r13 visual
 * M3: "cobranca@financeiro.vidaplena.com.br" vira "c•••@f•••.v•••.com.br"; antes só o primeiro rótulo era mascarado e o
 * domínio entregava a empresa que as iniciais escondem).
 */
/** Rodada rval1-2 (VIS-B2): nome de usuário (login de quem não tem e-mail) como a parte local do e-mail, `v•••`. */
export function mascararUsuario(usuario: string) {
  const u = usuario.trim();
  return u ? `${[...u].slice(0, 1).join('')}${P.repeat(3)}` : OCULTO;
}

export function mascararEmail(email: string) {
  const m = /^([^@\s]+)@([^@\s]+)$/.exec(email.trim());
  if (!m) return OCULTO;
  const [, usuario, dominio] = m as unknown as [string, string, string];
  const partes = dominio.split('.');
  const nSufixo = rotulosDoSufixo(partes);
  const corte = partes.length - nSufixo;
  const marcados = partes.slice(0, corte).map((p) => `${[...p].slice(0, 1).join('')}${P.repeat(3)}`);
  return `${[...usuario].slice(0, 1).join('')}${P.repeat(3)}@${[...marcados, ...partes.slice(corte)].join('.')}`;
}

/** Iniciais de um nome ("Maria da Silva Ltda" vira "M. S. L."). */
export function iniciais(nome: string) {
  const palavras = nome
    .trim()
    .split(/\s+/)
    .filter((p) => p.length > 2 || /^[A-ZÀ-Ý]/.test(p));
  const letras = (palavras.length ? palavras : [nome.trim()]).map((p) => p.slice(0, 1).toUpperCase()).filter(Boolean);
  return letras.length ? `${letras.slice(0, 4).join('. ')}.` : '';
}
